import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from 'src/redux/store';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';
import { setToNewZealandTimeZone } from 'src/utility/helper/dateHelper';
import {
    FishingTripRequest,
    FishingTripResponse,
    FishingTripsClient,
    TripCatchesClient,
    TripCatchRequest,
    TripCostRequest,
    TripCostsClient,
    TripCrewRequest,
    TripCrewsClient
} from 'src/utility/apis/crewPayApiClient';
import { AxiosError } from 'axios';
import { stringResources } from 'src/assets/config/stringResources';

export interface FishingTrip extends FishingTripResponse { }

export interface FishingTripModel extends FishingTripRequest {
    fishingTripId: number;
    copyDataFromTrip: boolean;
    copyDataFromTripId?: number;
    copyDataFromTripNumber?: string;
    copyDataFromTripPeriodId?: number;
    copyCatches: boolean;
    copyCrews: boolean;
    copyCosts: boolean;
}

export interface State {
    isLoading: boolean;
    fishingTripList: FishingTrip[];
    isFishingTripLoading: boolean;
    fishingTrip: FishingTrip | null;
    error: boolean;
    notFound: boolean;
}

const initialState: State = {
    isLoading: false,
    fishingTripList: [],
    isFishingTripLoading: true,
    fishingTrip: null,
    error: false,
    notFound: false,
};

const fishingTripSlice = createSlice({
    name: 'fishingTrip',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.isLoading = false;
            state.isFishingTripLoading = false;
            state.notFound = false;
            state.error = action.payload;
        },
        getFishingTripListSuccess: (state, action) => {
            state.fishingTripList = action.payload;
            state.isLoading = false;
        },
        startFishingTripLoading: (state) => {
            state.isFishingTripLoading = true;
            state.fishingTrip = null;
            state.notFound = false;
        },
        getFishingTripSuccess: (state, action: PayloadAction<FishingTrip>) => {
            state.isFishingTripLoading = false;
            state.fishingTrip = action.payload;
            state.notFound = false;
        },
        getFishingTripFailure: (state) => {
            state.isFishingTripLoading = false;
            state.fishingTrip = null;
            state.notFound = false;
        },
        getFishingTripNotFound: (state) => {
            state.isFishingTripLoading = false;
            state.notFound = true;
        },
        addNewFishingTripSuccess: (state, action: PayloadAction<FishingTrip>) => {
            const newItem = action.payload;
            state.fishingTripList = [newItem, ...state.fishingTripList];
            state.fishingTrip = newItem;
            toast.success(`'${newItem.fishingTripNumber}' created successfully!`);
        },
        updateFishingTripSuccess: (state, action: PayloadAction<FishingTrip>) => {
            const updatedItem = action.payload;
            const index = state.fishingTripList.findIndex(item => item.fishingTripId === updatedItem.fishingTripId);
            state.fishingTripList = [...state.fishingTripList.slice(0, index), updatedItem, ...state.fishingTripList.slice(index + 1)];
            state.fishingTrip = updatedItem;
            if (updatedItem.isCompleted) {
                toast.success(` '${updatedItem.fishingTripNumber}' completed successfully!`);
            } else {
                toast.success(` '${updatedItem.fishingTripNumber}' updated successfully!`);
            }
        },
        deleteFishingTripSuccess: (state, action: PayloadAction<number>) => {
            const deleteId = action.payload;
            if (state.fishingTrip) {
                state.fishingTripList = state.fishingTripList.filter(item => item.fishingTripId !== deleteId);
                toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
            }
        },
    },
});

// Reducer
export default fishingTripSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getFishingTripListSuccess,
} = fishingTripSlice.actions;

// for useSelector
export const selectFishingTrip = (state: RootState) => state.fishingTrip as State;

// ----------------------------------------------------------------------
export function getAllFishingTrip() {
    return async (dispatch, getState) => {
        dispatch(fishingTripSlice.actions.startLoading());
        try {
            const client = new FishingTripsClient(undefined, axios);
            const response = await client.getAll();
            dispatch(fishingTripSlice.actions.getFishingTripListSuccess(response));
        } catch (error) {
            dispatch(fishingTripSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function getFishingTrip(id: number, ignoreLoading: boolean = false) {
    return async (dispatch, getState) => {
        if (!ignoreLoading) {
            dispatch(fishingTripSlice.actions.startFishingTripLoading());
        }
        try {
            const client = new FishingTripsClient(undefined, axios);
            const response = await client.get(id);
            dispatch(fishingTripSlice.actions.getFishingTripSuccess(response));
            return response;
        } catch (error: any) {
            dispatch(fishingTripSlice.actions.getFishingTripFailure());
            if ((error as AxiosError) && error.status === 404) {
                dispatch(fishingTripSlice.actions.getFishingTripNotFound());
            } else {
                handleError(error);
            }
        }
    };
}

export function createFishingTrip(fishingTrip: FishingTripModel) {
    async function copyTripDetails(copyDataFromTripId: number, copyDataFromTripPeriodId: number, newFishingTripPeriodId: number,
                                   copyCatch: boolean, copyCrew: boolean, copyCosts: boolean) {
        if (copyCatch) {
            const client = new TripCatchesClient(undefined, axios);
            const tripCatches = await client.getByFishingTrip(copyDataFromTripId);
            const request: TripCatchRequest[] = [];
            for (const tripCatch of tripCatches.filter(c => c.fishingTripPeriodId === copyDataFromTripPeriodId)) {
                request.push({
                    fishingTripPeriodId: newFishingTripPeriodId,
                    productId: tripCatch.productId,
                    tripCatchDate: tripCatch.tripCatchDate,
                    productSpeciesId: tripCatch.productSpeciesId,
                    productQualityId: tripCatch.productQualityId,
                    productStateId: tripCatch.productStateId,
                    productSizeId: tripCatch.productSizeId,
                    amountCaughtKg: tripCatch.amountCaughtKg,
                    ratePerKg: tripCatch.ratePerKg,
                } as TripCatchRequest);
            }
            await client.createBatch(request);
        }

        if (copyCrew) {
            const client = new TripCrewsClient(undefined, axios);
            const tripCrews = await client.getByFishingTrip(copyDataFromTripId);
            const request: TripCrewRequest[] = [];
            for (const tripCrew of tripCrews.filter(c => c.fishingTripPeriodId === copyDataFromTripPeriodId)) {
                request.push({
                    fishingTripPeriodId: newFishingTripPeriodId,
                    workerId: tripCrew.workerId,
                    positionId: tripCrew.positionId,
                    isOnBoard: tripCrew.isOnBoard,
                    assignmentStatusId: tripCrew.assignmentStatusId,
                    share: tripCrew.share,
                    costShare: tripCrew.costShare,
                    dailyRate: tripCrew.dailyRate,
                    interimDailyRate: tripCrew.interimDailyRate,
                    interimPayPercentage: tripCrew.interimPayPercentage
                } as TripCrewRequest);
            }
            await client.createBatch(request);
        }

        if (copyCosts) {
            const client = new TripCostsClient(undefined, axios);
            const tripCosts = await client.getByFishingTrip(copyDataFromTripId);
            const request: TripCostRequest[] = [];
            for (const tripCost of tripCosts.filter(c => c.fishingTripPeriodId === copyDataFromTripPeriodId)) {
                request.push({
                    fishingTripPeriodId: newFishingTripPeriodId,
                    description: tripCost.description,
                    quantity: tripCost.quantity,
                    rate: tripCost.rate,
                } as TripCostRequest);
            }
            await client.createBatch(request);
        }
    }

        return async (dispatch, getState) => {
            try {
                const {
                    fishingTripId,
                    copyDataFromTrip,
                    copyDataFromTripId,
                    copyDataFromTripPeriodId,
                    copyCatches,
                    copyCrews,
                    copyCosts,
                    ...payload
                } = fishingTrip;
                const request = payload as FishingTripRequest;
                setDateFormat(request);
                const client = new FishingTripsClient(undefined, axios);
                const response = await client.create(request);
                if (copyDataFromTripId && copyDataFromTripId > 0 && copyDataFromTripPeriodId && copyDataFromTripPeriodId > 0) {
                    const newFishingTripPeriodId = response.fishingTripPeriod[0].fishingTripPeriodId;
                    await copyTripDetails(copyDataFromTripId, copyDataFromTripPeriodId, newFishingTripPeriodId, copyCatches, copyCrews, copyCosts);
                }
                dispatch(fishingTripSlice.actions.addNewFishingTripSuccess(response));
            } catch (error) {
                dispatch(fishingTripSlice.actions.hasError(true));
                handleError(error);
            }
        };
    
}

function setDateFormat(request: FishingTripRequest) {
    request.fishingTripStartDate = setToNewZealandTimeZone(request.fishingTripStartDate);
    if (request.fishingTripEndDate) {
        request.fishingTripEndDate = setToNewZealandTimeZone(request.fishingTripEndDate);
    }
    if (request.completedDate) {
        request.completedDate = setToNewZealandTimeZone(request.completedDate);
    }
    if (request.landingDate) {
        request.landingDate = setToNewZealandTimeZone(request.landingDate);
    }
}

export function updateFishingTrip(fishingTrip: FishingTripModel) {
    return async (dispatch, getState) => {
        try {
            const { fishingTripId, ...payload } = fishingTrip;
            const request = payload as FishingTripRequest;
            setDateFormat(request);
            const client = new FishingTripsClient(undefined, axios);
            const response = await client.edit(fishingTripId, request);
            dispatch(fishingTripSlice.actions.updateFishingTripSuccess(response));
        } catch (error) {
            dispatch(fishingTripSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function completeFishingTrip(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        try {
            const client = new FishingTripsClient(undefined, axios);
            const response = await client.complete(fishingTripNumber);
            dispatch(fishingTripSlice.actions.updateFishingTripSuccess(response));
        } catch (error) {
            dispatch(fishingTripSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function unCompleteFishingTrip(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        try {
            const client = new FishingTripsClient(undefined, axios);
            const response = await client.unComplete(fishingTripNumber);
            dispatch(fishingTripSlice.actions.updateFishingTripSuccess(response));
        } catch (error) {
            dispatch(fishingTripSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function deleteFishingTrip(fishingTripId: number) {
    return async (dispatch, getState) => {
        try {
            const client = new FishingTripsClient(undefined, axios);
            await client.delete(fishingTripId);
            dispatch(fishingTripSlice.actions.deleteFishingTripSuccess(fishingTripId));
        } catch (error) {
            dispatch(fishingTripSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

// This fishing trip load by id will not update state
export const pollingFromFishingTrip = async (id: number) => {
    try {
        const client = new FishingTripsClient(undefined, axios);
        return await client.get(id);
    } catch (error: any) {
        handleError(error);
    }
}